import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Button,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    CircularProgress,
    Tooltip,
} from '@mui/material'
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Download as DownloadIcon,
    Search as SearchIcon,
    Email as EmailIcon,
} from '@mui/icons-material'
import { useAppStore, useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import moment from 'moment-timezone'
import { InvoiceResponse, clientInvoicesApi, InvoiceStatus } from '@assembly/api/clientInvoices'
import { RoutePaths } from '@assembly/constants'
import { interpolateRouteUtil } from '@assembly/utils'
import CreateInvoiceDialog from './CreateInvoiceDialog'
import EditInvoiceDialog from './EditInvoiceDialog'
import ViewInvoiceDialog from './ViewInvoiceDialog'
import EmailInvoiceModal from './EmailInvoiceModal'
import ConfirmDeleteDialog from '@assembly/components/ConfirmDeleteDialog'

const ITEMS_PER_PAGE = 20

export default function ClientInvoices() {
    const { setAppBarTitle } = useAppStore()
    const { addAlert } = useSnackbars()
    const navigate = useNavigate()

    const [invoices, setInvoices] = useState<InvoiceResponse[]>([])
    const [loading, setLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE)

    // Search and filters
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('')
    const [typeFilter, setTypeFilter] = useState<string>('')

    // Dialogs
    const [createDialogOpen, setCreateDialogOpen] = useState(false)
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [viewDialogOpen, setViewDialogOpen] = useState(false)
    const [emailDialogOpen, setEmailDialogOpen] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [selectedInvoice, setSelectedInvoice] = useState<InvoiceResponse | null>(null)
    const [deleteInputValue, setDeleteInputValue] = useState('')
    const [isDeleting, setIsDeleting] = useState(false)

    useEffect(() => {
        setAppBarTitle('Client Invoices')
        fetchInvoices()
    }, [page, pageSize, statusFilter, typeFilter])

    const fetchInvoices = async () => {
        try {
            setLoading(true)

            // Build search parameters
            const searchParams: any = {
                page,
                pageSize,
            }

            if (statusFilter) {
                searchParams.status = statusFilter
            }

            if (typeFilter) {
                searchParams.type = typeFilter
            }

            if (searchTerm) {
                searchParams.searchText = searchTerm
            }

            // Use the searchInvoices API endpoint
            const response = await clientInvoicesApi.searchInvoices(searchParams)

            // Ensure we have valid data before setting state
            if (response.data && response.data.invoices) {
                setInvoices(response.data.invoices)
                setTotalCount(response.data.totalCount || 0)
            } else {
                setInvoices([])
                setTotalCount(0)
            }
        } catch (error) {
            addAlert({ message: 'Failed to fetch invoices', type: SnackbarType.Error })
            console.error('Error fetching invoices:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = () => {
        setPage(0)
        fetchInvoices()
    }

    const handleStatusFilterChange = (event: SelectChangeEvent) => {
        setStatusFilter(event.target.value)
        setPage(0)
    }

    const handleTypeFilterChange = (event: SelectChangeEvent) => {
        setTypeFilter(event.target.value)
        setPage(0)
    }

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPageSize(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleCreateInvoice = () => {
        setCreateDialogOpen(true)
    }

    const handleEditInvoice = (invoice: InvoiceResponse) => {
        setSelectedInvoice(invoice)
        setEditDialogOpen(true)
    }

    const handleViewInvoice = (invoice: InvoiceResponse) => {
        if (invoice.id) {
            navigate(interpolateRouteUtil(RoutePaths.ClientInvoice, { id: invoice.id }))
        }
    }

    const handleDeleteInvoice = (invoice: InvoiceResponse) => {
        setSelectedInvoice(invoice)
        setDeleteDialogOpen(true)
    }

    const handleEmailInvoice = (invoice: InvoiceResponse) => {
        setSelectedInvoice(invoice)
        setEmailDialogOpen(true)
    }

    const handleExportPDF = async (invoice: InvoiceResponse) => {
        try {
            const { getPDFGeneratorType } = await import('@assembly/utils/pdf/pdfConfig')
            const generatorType = getPDFGeneratorType()

            if (generatorType === 'optimized') {
                // Use optimized PDF generator for smaller file sizes (~50-200KB)
                const { downloadOptimizedInvoicePDF } = await import('@assembly/utils/pdf/optimizedInvoiceGenerator')
                downloadOptimizedInvoicePDF(invoice)
            } else {
                // Use HTML-based PDF generator for original styling (~20-30MB)
                const { downloadInvoiceHTMLPDF } = await import('@assembly/utils/pdf/htmlInvoiceGenerator')
                await downloadInvoiceHTMLPDF(invoice)
            }

            addAlert({ message: 'PDF exported successfully', type: SnackbarType.Success })
        } catch (error) {
            addAlert({ message: 'Failed to export PDF', type: SnackbarType.Error })
            console.error('Error exporting PDF:', error)
        }
    }

    const getStatusColor = (status: InvoiceStatus) => {
        switch (status) {
            case 'PAID':
                return 'success'
            case 'PARTIALLY_PAID':
                return 'info'
            case 'PENDING':
                return 'warning'
            case 'SENT':
                return 'info'
            case 'OVERDUE':
                return 'error'
            case 'DRAFT':
                return 'default'
            case 'CANCELLED':
                return 'error'
            case 'REFUNDED':
                return 'warning'
            case 'ERROR':
                return 'error'
            default:
                return 'default'
        }
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount)
    }

    const formatDate = (timestamp: number) => {
        return moment(timestamp).format('MMM DD, YYYY')
    }

    if (loading && (!invoices || invoices.length === 0)) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box p={3}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    Client Invoices
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateInvoice}
                >
                    Create Invoice
                </Button>
            </Box>

            {/* Search and Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Search invoices..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            InputProps={{
                                endAdornment: (
                                    <IconButton onClick={handleSearch}>
                                        <SearchIcon />
                                    </IconButton>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status"
                                onChange={handleStatusFilterChange}
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="DRAFT">Draft</MenuItem>
                                <MenuItem value="PENDING">Pending</MenuItem>
                                <MenuItem value="SENT">Sent</MenuItem>
                                <MenuItem value="PAID">Paid</MenuItem>
                                <MenuItem value="PARTIALLY_PAID">Partially Paid</MenuItem>
                                <MenuItem value="OVERDUE">Overdue</MenuItem>
                                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                                <MenuItem value="REFUNDED">Refunded</MenuItem>
                                <MenuItem value="ERROR">Error</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={typeFilter}
                                label="Type"
                                onChange={handleTypeFilterChange}
                            >
                                <MenuItem value="">All Types</MenuItem>
                                <MenuItem value="ONE_TIME">One Time</MenuItem>
                                <MenuItem value="MONTHLY">Monthly</MenuItem>
                                <MenuItem value="PRORATED">Prorated</MenuItem>
                                <MenuItem value="CLOSING">Closing</MenuItem>
                                <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                                <MenuItem value="INTERNAL">Internal</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Invoices Table */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Invoice #</TableCell>
                                <TableCell>Organization</TableCell>
                                <TableCell>Project</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Due Date</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices && Array.isArray(invoices) && invoices.length > 0 ? invoices.map((invoice) => (
                                invoice && invoice.id && (
                                    <TableRow key={invoice.id} hover>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                fontWeight="medium"
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: 'primary.main',
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                                onClick={() => handleViewInvoice(invoice)}
                                            >
                                                {invoice.invoiceNumber ? invoice.invoiceNumber : 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {invoice.organization && invoice.organization.name ? invoice.organization.name : 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {invoice.assemblyLine && invoice.assemblyLine.name ? invoice.assemblyLine.name : 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={invoice.type ? invoice.type : 'N/A'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={invoice.status ? invoice.status.toString() : 'N/A'}
                                                color={getStatusColor(invoice.status || 'DRAFT') as any}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {invoice.totalAmount !== null && invoice.totalAmount !== undefined ? formatCurrency(invoice.totalAmount) : 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {invoice.dueDate ? formatDate(invoice.dueDate) : 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={1}>
                                                <Tooltip title="View Invoice">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => invoice && handleViewInvoice(invoice)}
                                                        disabled={!invoice}
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit Invoice">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => invoice && handleEditInvoice(invoice)}
                                                        disabled={!invoice}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Send Email">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => invoice && handleEmailInvoice(invoice)}
                                                        disabled={!invoice}
                                                        color="primary"
                                                    >
                                                        <EmailIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Export PDF">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => invoice && handleExportPDF(invoice)}
                                                        disabled={!invoice}
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Invoice">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => invoice && handleDeleteInvoice(invoice)}
                                                        color="error"
                                                        disabled={!invoice}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {loading ? 'Loading invoices...' : 'No invoices found'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={pageSize}
                    page={page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePageSizeChange}
                />
            </Paper>

            {/* Dialogs */}
            <CreateInvoiceDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSuccess={() => {
                    setCreateDialogOpen(false)
                    fetchInvoices()
                }}
            />

            <EditInvoiceDialog
                open={editDialogOpen}
                invoice={selectedInvoice}
                onClose={() => setEditDialogOpen(false)}
                onSuccess={() => {
                    setEditDialogOpen(false)
                    fetchInvoices()
                }}
            />

            <ViewInvoiceDialog
                open={viewDialogOpen}
                invoice={selectedInvoice}
                onClose={() => setViewDialogOpen(false)}
                onEdit={() => {
                    setViewDialogOpen(false)
                    handleEditInvoice(selectedInvoice!)
                }}
                onExportPDF={() => {
                    setViewDialogOpen(false)
                    if (selectedInvoice) handleExportPDF(selectedInvoice)
                }}
                onEmail={() => {
                    setViewDialogOpen(false)
                    if (selectedInvoice) handleEmailInvoice(selectedInvoice)
                }}
            />

            <EmailInvoiceModal
                open={emailDialogOpen}
                invoice={selectedInvoice}
                onClose={() => setEmailDialogOpen(false)}
            />

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                name={selectedInvoice?.invoiceNumber || ''}
                onClickDelete={async () => {
                    if (selectedInvoice) {
                        try {
                            setIsDeleting(true)
                            await clientInvoicesApi.deleteInvoice(selectedInvoice.id)
                            addAlert({ message: 'Invoice deleted successfully', type: SnackbarType.Success })
                            setDeleteDialogOpen(false)
                            setDeleteInputValue('')
                            fetchInvoices()
                        } catch (error) {
                            addAlert({ message: 'Failed to delete invoice', type: SnackbarType.Error })
                        } finally {
                            setIsDeleting(false)
                        }
                    }
                }}
                isLoading={isDeleting}
                inputValue={deleteInputValue}
                onChangeInput={(e) => setDeleteInputValue(e.target.value)}
            />
        </Box>
    )
}
